import {
  type FccServiceCancellationStateEnum,
  FccServiceCancellationTypeEnum,
} from '@backoffice-frontend/graphql/subscription'
import {
  Body1,
  Divider,
  MoiaGrid,
  MoiaLabelCheckbox,
} from '@backoffice-frontend/patterns'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@moia-dev/pace-web'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ServiceCancellationInitiatedAction } from '../../../ServiceCancellation/ServiceCancellationProvider'
import { useServiceCancellation } from '../../../ServiceCancellation/useServiceCancellation'
import { ServiceCancellationCheck } from './ServiceCancellationCheck'

export type ServiceCancellationInteractionProps = {
  vehicleId: string
  vehicleIsStopped: boolean
  isStopRequired: boolean
  cancellationState?: FccServiceCancellationStateEnum
  skipTelematicsCheck: boolean
  setSkipTelematicsCheck: (value: boolean) => void
}

export const ServiceCancellationInteraction = ({
  vehicleId,
  vehicleIsStopped,
  isStopRequired,
  skipTelematicsCheck,
  setSkipTelematicsCheck,
}: ServiceCancellationInteractionProps) => {
  const { t } = useTranslation()

  const {
    initiate,
    cancelAllTrips,
    continueAllTrips,
    state: serviceCancellationState,
    decision,
    confirmRestoreMission,
  } = useServiceCancellation()

  const requirementsMet =
    !isStopRequired || vehicleIsStopped || skipTelematicsCheck

  const canAttemptCancellation =
    serviceCancellationState.matches('initiated') ||
    serviceCancellationState.matches({
      completed: 'error',
    })

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on mount
  useEffect(() => {
    initiate(
      vehicleId,
      FccServiceCancellationTypeEnum.AllTrips,
      ServiceCancellationInitiatedAction.CancelService,
    )
  }, [])

  return (
    <MoiaGrid
      css={theme => ({
        margin: `${theme.space.Space4} ${theme.space.Space6}`,
      })}
    >
      <MoiaGrid column gridTemplateColumns="1fr">
        <MoiaGrid
          column
          gridGap={2}
          alignItems="center"
          gridTemplateColumns="auto 1fr"
          css={theme => ({
            borderRight: `1px solid ${theme.semantic.ColorAccessoryDefault}`,
          })}
        >
          {isStopRequired && requirementsMet && (
            <>
              <div
                css={theme => ({
                  width: 3,
                  height: 50,
                  borderRadius: 2,
                  backgroundColor: theme.semantic.ColorCriticalDefault,
                })}
              />
              <Body1>
                {t('adFleetMap_serviceCancellationRequirementsMet_label')}
                <br />
                {t('adFleetMap_proceedWithServiceCancellation_label')}
              </Body1>
            </>
          )}
        </MoiaGrid>
        <MoiaGrid alignItems="center">
          <Button
            variant="secondary"
            onClick={continueAllTrips}
            label={t('map_dismiss_button')}
            disabled={!requirementsMet || !canAttemptCancellation}
          />
          <Tooltip placement="top">
            <TooltipTrigger>
              <Button
                variant="secondary"
                onClick={confirmRestoreMission}
                label={t('common_restoreMission_button')}
                disabled={!requirementsMet || !canAttemptCancellation}
              />
            </TooltipTrigger>
            <TooltipContent css={{ zIndex: 9999 }}>
              {t('adFleetMap_restoreMissionAfterSDSCrash_label')}
            </TooltipContent>
          </Tooltip>

          <Button
            variant="critical"
            onClick={cancelAllTrips}
            label={t('common_cancelService_button')}
            disabled={!requirementsMet || !canAttemptCancellation}
          />
        </MoiaGrid>
      </MoiaGrid>
      <ServiceCancellationCheck
        serviceCancellationState={serviceCancellationState}
        decision={decision}
      />
      {isStopRequired && (
        <>
          <Divider borderStyle="dashed" />
          <MoiaLabelCheckbox
            label={t('map_skipTelematics_caption')}
            checked={skipTelematicsCheck}
            onChange={() => setSkipTelematicsCheck(!skipTelematicsCheck)}
            disabled={vehicleIsStopped}
          />
        </>
      )}
    </MoiaGrid>
  )
}
